import { useId, useState } from 'react'

import { DistributionChannel } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { ButtonGroup } from '@avantstay/backoffice-core'

import { MessageDestination } from '../../StayTimeline.types'
import { NoteComposer } from './components/NoteComposer'
import { ReplyComposer } from './components/ReplyComposer'
import * as S from './MessageComposer.styles'

interface MessageComposerProps {
  className?: string
  guestName?: string
  otaChannel?: DistributionChannel
  isHidden?: boolean
  isSending: boolean
  isAddingNote?: boolean
  onSend: (message: string, destination: MessageDestination) => Promise<boolean>
  onAddNote?: (body: string) => Promise<boolean>
}

type ComposerMode = 'reply' | 'note'

export const MessageComposer = ({
  className,
  guestName = 'guest',
  otaChannel,
  isHidden = false,
  isSending,
  isAddingNote = false,
  onSend,
  onAddNote,
}: MessageComposerProps) => {
  const [draft, setDraft] = useState('')
  const [mode, setMode] = useState<ComposerMode>('reply')
  const modeGroupName = useId()

  const isNote = mode === 'note'
  const isBusy = isSending || isAddingNote

  const replyComposer = (
    <ReplyComposer
      guestName={guestName}
      otaChannel={otaChannel}
      draft={draft}
      isSending={isSending}
      onDraftChange={setDraft}
      onSend={onSend}
    />
  )

  if (!onAddNote) {
    return (
      <S.Root className={className} $isHidden={isHidden}>
        {replyComposer}
      </S.Root>
    )
  }

  return (
    <S.Root className={className} $isHidden={isHidden}>
      <S.ModeSwitch role="group" aria-label="Message type">
        <ButtonGroup
          name={modeGroupName}
          currentSelection={mode}
          options={[
            { value: 'reply', title: 'Reply to guest', disabled: isBusy },
            { value: 'note', title: 'Internal note', disabled: isBusy },
          ]}
          onClick={setMode}
        />
      </S.ModeSwitch>

      <S.Variants>
        <S.Variant $isActive={!isNote} aria-hidden={isNote}>
          {replyComposer}
        </S.Variant>
        <S.Variant $isActive={isNote} aria-hidden={!isNote}>
          <NoteComposer
            draft={draft}
            isAddingNote={isAddingNote}
            onDraftChange={setDraft}
            onAddNote={onAddNote}
          />
        </S.Variant>
      </S.Variants>
    </S.Root>
  )
}
